import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Edit, Network, Building2, Shield, UserPlus, ArrowRight } from 'lucide-react';
import Tabs, { type Tab } from '../../components/common/Tabs';
import DataTable from '../../components/common/DataTable';
import type { Column } from '../../components/common/DataTable';
import Modal from '../../components/common/Modal';
import ChannelModal, { type ChannelFormData } from '../../components/admin/ChannelModal';
import channelService, { CHANNEL_STATUS, type Channel } from '../../services/channel.service';
import type { Tenant } from '../../services/tenant.service';
import userService from '../../services/user.service';
import type { User as ApiUser } from '../../services/user.service';
import { useAuthStore } from '../../stores/authStore';
import { useTenantStore } from '../../stores/tenantStore';
import { USER_TYPES, MAX_PAGE_SIZE } from '../../utils/constants';
import { useToast } from '../../components/common/ToastContainer';
import { apiErrorMessage } from '../../utils/apiError';

/**
 * Channel detail — Overview, the channel's tenants, and (SuperAdmin only) its
 * administrators.
 *
 * The Tenants tab is a ChannelAdmin's primary workflow: pick a tenant here and
 * the topbar scope switches to it, which is what makes the rest of the app
 * usable for them. Everything write-shaped is gated on SuperAdmin — a
 * ChannelAdmin is read-only on channels by design.
 */
const ChannelDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const user = useAuthStore((s) => s.user);
  const setSelectedTenantId = useTenantStore((s) => s.setSelectedTenantId);
  const isSuperAdmin = user?.type === USER_TYPES.SUPER_ADMIN;

  const [channel, setChannel] = useState<Channel | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [admins, setAdmins] = useState<ApiUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);

  // Not memoised on `toast` — the toast context object is recreated per render,
  // so a useCallback over it would re-fire the effect on every render.
  const load = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setChannel(await channelService.getById(id));
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load channel details'));
    } finally {
      setLoading(false);
    }

    try {
      setTenantsLoading(true);
      setTenants(await channelService.getTenants(id));
    } catch (err) {
      toast.error('Load Failed', apiErrorMessage(err, 'Failed to load the channel’s tenants'));
    } finally {
      setTenantsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // Channel administrators are only listable by a SuperAdmin — a ChannelAdmin
  // gets 403 on /users, so don't even ask.
  useEffect(() => {
    if (!isSuperAdmin) return;
    userService
      .list({ type: USER_TYPES.CHANNEL_ADMIN, pageSize: MAX_PAGE_SIZE })
      .then((res) => setAdmins(res.items))
      .catch(() => setAdmins([]));
  }, [isSuperAdmin]);

  const handleSaveChannel = async (data: ChannelFormData) => {
    if (!id) return;
    try {
      await channelService.update(id, {
        name: data.name,
        slug: data.slug || undefined,
        description: data.description || undefined,
        contactEmail: data.contactEmail || undefined,
        status: data.status,
      });
      setChannel(await channelService.getById(id));
      setIsEditModalOpen(false);
      toast.success('Channel Updated', `${data.name} updated successfully`);
    } catch (err) {
      toast.error('Update Failed', apiErrorMessage(err, 'Failed to update channel'));
    }
  };

  const handleAssignAdmin = async (userId: string) => {
    if (!id) return;
    try {
      await channelService.assignAdmin(id, userId);
      toast.success('Administrator Assigned', 'The user now administers this channel.');
      setIsAssignModalOpen(false);
    } catch (err) {
      toast.error('Assign Failed', apiErrorMessage(err, 'Failed to assign channel administrator'));
    }
  };

  // Switching scope needs a full reload, for the same reason the topbar picker
  // does: in-flight fetches and any tenant-scoped route are bound to the old
  // tenant. See TenantSelector.handleTenantChange.
  const handleManageTenant = (tenant: Tenant) => {
    setSelectedTenantId(tenant.id);
    window.location.href = '/dashboard';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading channel details...</div>;
  }

  if (error || !channel) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-600">{error || 'Channel not found'}</div>
      </div>
    );
  }

  const tenantColumns: Column<Tenant>[] = [
    {
      key: 'name',
      label: 'Tenant',
      sortable: true,
      render: (t) => (
        <div className="flex items-center space-x-2">
          <Building2 className="w-4 h-4 text-primary-600" />
          <span className="font-medium">{t.name}</span>
        </div>
      ),
    },
    { key: 'domain', label: 'Domain', sortable: true, render: (t) => <span className="text-gray-600">{t.domain || '-'}</span> },
    {
      key: 'description',
      label: 'Description',
      render: (t) => <span className="text-gray-600">{t.description || '-'}</span>,
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (t) => (
        <div className="flex justify-end">
          <button
            onClick={() => handleManageTenant(t)}
            className="btn-secondary inline-flex items-center space-x-2 py-1 px-2 text-sm"
            title="Switch scope to this tenant"
          >
            <span>Manage</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      ),
    },
  ];

  const adminColumns: Column<ApiUser>[] = [
    { key: 'email', label: 'Email', sortable: true, render: (u) => u.email || u.username },
    { key: 'userRealName', label: 'Name', sortable: true, render: (u) => u.userRealName || '-' },
    {
      key: 'status',
      label: 'Status',
      render: (u) => (
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full ${
            (u.status ?? 0) === 0 ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-700'
          }`}
        >
          {(u.status ?? 0) === 0 ? 'Active' : 'Inactive'}
        </span>
      ),
    },
  ];

  const field = (label: string, value: React.ReactNode) => (
    <div>
      <label className="block text-sm font-medium text-gray-500 mb-1">{label}</label>
      <p className="text-gray-900">{value}</p>
    </div>
  );

  const tabs: Tab[] = [
    {
      id: 'overview',
      label: 'Overview',
      icon: Network,
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="card space-y-3">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Channel Information</h3>
            {field('Name', channel.name)}
            {field('Slug', channel.slug || '-')}
            {field('Contact Email', channel.contactEmail || '-')}
            {field(
              'Status',
              <span
                className={`px-2 py-1 text-xs font-medium rounded-full ${
                  channel.status === CHANNEL_STATUS.ACTIVE
                    ? 'bg-success-100 text-success-700'
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                {channel.status === CHANNEL_STATUS.ACTIVE ? 'Active' : 'Suspended'}
              </span>
            )}
          </div>
          <div className="card space-y-3">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Details</h3>
            {field('Description', channel.description || '-')}
            {field('Tenants', tenants.length)}
            {field('Created', channel.createTime || '-')}
          </div>
        </div>
      ),
    },
    {
      id: 'tenants',
      label: `Tenants (${tenants.length})`,
      icon: Building2,
      content: (
        <div className="space-y-4">
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
            <p className="text-sm text-primary-800">
              Tenants owned by this channel. <strong>Manage</strong> switches your topbar scope to
              that tenant so the rest of the console operates within it.
            </p>
          </div>
          <DataTable
            columns={tenantColumns}
            data={tenants}
            searchPlaceholder="Search tenants..."
            loading={tenantsLoading}
          />
        </div>
      ),
    },
  ];

  if (isSuperAdmin) {
    tabs.push({
      id: 'administrators',
      label: 'Administrators',
      icon: Shield,
      content: (
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start justify-between space-x-4">
            <p className="text-sm text-amber-800">
              <strong>Channel Administrators</strong> manage the tenants, users and edges inside
              this channel without gaining global access. The user must{' '}
              <strong>already exist as a Channel Administrator</strong> — assigning here grants
              membership, it does not create the account.
            </p>
            <button
              onClick={() => setIsAssignModalOpen(true)}
              className="btn-primary inline-flex items-center space-x-2 whitespace-nowrap"
            >
              <UserPlus className="w-4 h-4" />
              <span>Assign Admin</span>
            </button>
          </div>
          <DataTable
            columns={adminColumns}
            data={admins}
            searchPlaceholder="Search channel administrators..."
          />
        </div>
      ),
    });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/admin/channels')}
            className="icon-button"
            aria-label="Back to channels"
          >
            <ArrowLeft className="w-5 h-5 text-secondary-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{channel.name}</h1>
            <p className="text-gray-600 mt-1">
              {channel.slug ? `${channel.slug} • ` : ''}
              {tenants.length} tenant{tenants.length === 1 ? '' : 's'}
            </p>
          </div>
        </div>
        {isSuperAdmin && (
          <button
            onClick={() => setIsEditModalOpen(true)}
            className="btn-secondary inline-flex items-center space-x-2"
          >
            <Edit className="w-4 h-4" />
            <span>Edit Channel</span>
          </button>
        )}
      </div>

      <div className="card">
        <Tabs tabs={tabs} defaultTab="overview" />
      </div>

      {isSuperAdmin && (
        <>
          <ChannelModal
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            onSave={handleSaveChannel}
            channel={{
              id: channel.id,
              name: channel.name,
              slug: channel.slug || '',
              description: channel.description || '',
              contactEmail: channel.contactEmail || '',
              status: channel.status,
            }}
            mode="edit"
          />

          <AssignAdminModal
            key={isAssignModalOpen ? 'assign-open' : 'assign-closed'}
            isOpen={isAssignModalOpen}
            onClose={() => setIsAssignModalOpen(false)}
            onAssign={handleAssignAdmin}
            candidates={admins}
          />
        </>
      )}
    </div>
  );
};

/**
 * Picker over existing type-5 users. There is deliberately no "create a
 * ChannelAdmin" path here: the provisioning endpoint rejects userType 5, so a
 * type-5 account is created out-of-band and only then granted membership.
 */
const AssignAdminModal = ({
  isOpen,
  onClose,
  onAssign,
  candidates,
}: {
  isOpen: boolean;
  onClose: () => void;
  onAssign: (userId: string) => void;
  candidates: ApiUser[];
}) => {
  // Remounted by the caller's `key` when the dialog opens, so the selection
  // starts empty without an effect writing state.
  const [selected, setSelected] = useState('');

  const footer = (
    <div className="flex items-center justify-end space-x-3">
      <button onClick={onClose} className="btn-secondary">
        Cancel
      </button>
      <button onClick={() => onAssign(selected)} className="btn-primary" disabled={!selected}>
        Assign
      </button>
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Assign Channel Administrator" footer={footer}>
      <div className="space-y-4">
        <label className="block text-sm font-medium text-gray-700">Channel Administrator</label>
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className="input-base w-full"
        >
          <option value="">Select a user…</option>
          {candidates.map((c) => (
            <option key={c.id} value={c.id}>
              {c.email || c.username}
              {c.userRealName ? ` (${c.userRealName})` : ''}
            </option>
          ))}
        </select>
        {candidates.length === 0 && (
          <p className="text-sm text-gray-500">
            No Channel Administrator accounts exist yet. One must be created before it can be
            assigned to a channel.
          </p>
        )}
      </div>
    </Modal>
  );
};

export default ChannelDetail;
